import { supabase } from '@/lib/supabase'
import type {
  CreateStoryInput,
  CreateStoryResult,
  StoryBankEntry,
  StoryDetail,
  StoryPatch,
} from '@/types/interview'
import { FunctionsHttpError } from '@supabase/supabase-js'
import { useCallback, useState } from 'react'

interface ListResponse {
  stories: StoryBankEntry[]
  storyCount: number
  isPro: boolean
  cap: number | null
}

export function useStoryBank() {
  const [stories, setStories] = useState<StoryBankEntry[]>([])
  const [storyCount, setStoryCount] = useState(0)
  const [isPro, setIsPro] = useState(false)
  const [cap, setCap] = useState<number | null>(null)
  const [loading, setLoading] = useState(false)
  // True once the first fetchStories() call has settled (success or failure).
  // `loading` alone races the mount effect — it starts false, so a consumer
  // gating on `!loading` can fire before any request has actually completed.
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const getToken = useCallback(async (): Promise<string | null> => {
    const {
      data: { session },
    } = await supabase.auth.getSession()
    return session?.access_token ?? null
  }, [])

  const fetchStories = useCallback(async () => {
    setLoading(true)
    setError(null)

    const token = await getToken()
    if (!token) {
      setError('Please sign in to view your Story Bank.')
      setLoading(false)
      setHasLoaded(true)
      return
    }

    const { data, error: invokeErr } = await supabase.functions.invoke<ListResponse>('story-bank', {
      body: { action: 'list' },
      headers: { Authorization: `Bearer ${token}` },
    })

    if (invokeErr || !data) {
      setError('We could not load your stories. Please try again.')
    } else {
      setStories(data.stories)
      setStoryCount(data.storyCount)
      setIsPro(data.isPro)
      setCap(data.cap)
    }
    setLoading(false)
    setHasLoaded(true)
  }, [getToken])

  const saveStory = useCallback(
    async (answerId: string, title: string): Promise<{ storyId: string } | null> => {
      const token = await getToken()
      if (!token) return null

      const { data, error: invokeErr } = await supabase.functions.invoke<{ storyId: string }>(
        'story-bank',
        {
          body: { action: 'save', answerId, title },
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (invokeErr || !data) return null
      return data
    },
    [getToken]
  )

  const createStory = useCallback(
    async (input: CreateStoryInput): Promise<CreateStoryResult> => {
      const token = await getToken()
      if (!token) return { ok: false, code: 'ERROR' }

      const { data, error: invokeErr } = await supabase.functions.invoke<{
        storyId: string
        storyCount: number
        cap: number | null
      }>('story-bank', {
        body: { action: 'create', ...input },
        headers: { Authorization: `Bearer ${token}` },
      })

      if (invokeErr) {
        // The cap response is a 403 with a typed JSON body
        if (invokeErr instanceof FunctionsHttpError) {
          try {
            const body = (await invokeErr.context.json()) as {
              code?: string
              storyCount?: number
              cap?: number
            }
            if (body.code === 'STORY_CAP_REACHED') {
              return {
                ok: false,
                code: 'STORY_CAP_REACHED',
                storyCount: body.storyCount ?? 3,
                cap: body.cap ?? 3,
              }
            }
          } catch {
            // fall through to generic error
          }
        }
        return { ok: false, code: 'ERROR' }
      }
      if (!data) return { ok: false, code: 'ERROR' }

      await fetchStories()
      return { ok: true, storyId: data.storyId, storyCount: data.storyCount, cap: data.cap }
    },
    [getToken, fetchStories]
  )

  const getStory = useCallback(
    async (storyId: string): Promise<StoryDetail | null> => {
      const token = await getToken()
      if (!token) return null

      const { data, error: invokeErr } = await supabase.functions.invoke<{ story: StoryDetail }>(
        'story-bank',
        {
          body: { action: 'get', storyId },
          headers: { Authorization: `Bearer ${token}` },
        }
      )

      if (invokeErr || !data) return null
      return data.story
    },
    [getToken]
  )

  const updateStory = useCallback(
    async (storyId: string, patch: StoryPatch): Promise<boolean> => {
      const token = await getToken()
      if (!token) return false

      // Optimistic patch of list-visible fields; revert via re-fetch on failure
      setStories((prev) =>
        prev.map((s) =>
          s.id === storyId
            ? {
                ...s,
                ...(patch.title !== undefined && { title: patch.title }),
                ...(patch.notes !== undefined && { notes: patch.notes }),
                ...(patch.isFavorite !== undefined && { isFavorite: patch.isFavorite }),
                ...(patch.roleTag !== undefined && { roleTag: patch.roleTag }),
                ...(patch.competency !== undefined && { competency: patch.competency }),
                ...(patch.star !== undefined && s.star
                  ? { star: { ...s.star, ...patch.star } }
                  : {}),
              }
            : s
        )
      )

      const { error: invokeErr } = await supabase.functions.invoke('story-bank', {
        body: { action: 'update', storyId, patch },
        headers: { Authorization: `Bearer ${token}` },
      })

      if (invokeErr) {
        void fetchStories()
        return false
      }
      return true
    },
    [getToken, fetchStories]
  )

  const deleteStory = useCallback(
    async (storyId: string): Promise<boolean> => {
      const token = await getToken()
      if (!token) return false

      // Optimistic removal
      setStories((prev) => prev.filter((s) => s.id !== storyId))
      setStoryCount((prev) => Math.max(0, prev - 1))

      const { error: invokeErr } = await supabase.functions.invoke('story-bank', {
        body: { action: 'delete', storyId },
        headers: { Authorization: `Bearer ${token}` },
      })

      if (invokeErr) {
        // Revert on failure — re-fetch to restore state
        void fetchStories()
        return false
      }
      return true
    },
    [getToken, fetchStories]
  )

  return {
    stories,
    storyCount,
    isPro,
    cap,
    loading,
    hasLoaded,
    error,
    fetchStories,
    saveStory,
    createStory,
    getStory,
    updateStory,
    deleteStory,
  }
}
